import React, { useState, useEffect } from 'react';
import { Button, Input, Select, Space, Typography } from 'antd';
import { AsYouType } from 'libphonenumber-js';
import firebase from 'firebase/app';

import { COUNTRIES, getPlaceholder, defaultCountry } from '../../utils/phone';
import { looksLikePhone, resolveIdentifier } from '../../utils/identity';

const { Text } = Typography;
const { Option } = Select;

const GOOG_RECAPTCHA_SITE_KEY = '6LdZWSYrAAAAAIeT9OanpWKafZysLd_gMis6EgD8';

const inputStyle = {
  backgroundColor: '#1a1a1a',
  color: '#fff',
  borderColor: '#444',
};

/**
 * Identity-first entry: the user gives an email or mobile number BEFORE picking a
 * provider, so we can route them to the account they already have instead of letting
 * them guess and mint a duplicate.
 *
 * `mode` is 'email' or 'phone' — the person chose which on the step before, so each
 * field is one thing: the mobile entry shows the country picker from the first
 * keystroke (it used to appear only once digits were typed, which hid the country
 * exactly when it mattered). `onSwitchMode`, when given, renders the "use my … instead"
 * link. Without a mode the field falls back to the old combined behaviour.
 *
 * Calls onResolved({ status, providers, hasPhoneCredential, identifier }) where status is
 * 'has_credential' | 'account_no_credential' | 'not_found'. onSkip reveals all providers.
 */
export default function ClientIdentityFirst({
  isSignup, onResolved, onSkip, defaultCountryCode, mode, onSwitchMode,
}) {
  const [raw, setRaw] = useState('');
  // defaultCountryCode: the landing's hint (a claim/join tag's payout currency →
  // country). It beats the device locale; the person can still change it.
  const [country, setCountry] = useState(() => defaultCountry(defaultCountryCode));
  const [countryTouched, setCountryTouched] = useState(false);
  // The hint can arrive after mount (the tag read takes a moment). Adopt it only
  // while nothing has been typed and the person hasn't picked a country themselves.
  useEffect(() => {
    if (defaultCountryCode && !raw && !countryTouched) setCountry(defaultCountry(defaultCountryCode));
  }, [defaultCountryCode]); // deliberately not on `raw`: the hint must not re-apply on later edits
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isPhone = mode === 'phone' || (!mode && looksLikePhone(raw));

  const handleChange = (e) => {
    const value = e.target.value;
    // Format phone input as the user types; leave emails untouched.
    if (mode === 'phone' ? !value.includes('@') : (!mode && looksLikePhone(value))) {
      const formatter = new AsYouType(country.code);
      setRaw(formatter.input(value));
    } else {
      setRaw(value);
    }
    setError(null);
  };

  const handleContinue = async () => {
    setError(null);
    const { identifier, error: validationError } = resolveIdentifier(raw, country.code, mode);
    if (validationError) { setError(validationError); return; }

    setLoading(true);
    try {
      await new Promise((resolve) => (window.grecaptcha?.ready ? window.grecaptcha.ready(resolve) : resolve()));
      let token = null;
      if (window.grecaptcha?.execute) {
        token = await window.grecaptcha.execute(GOOG_RECAPTCHA_SITE_KEY, { action: 'account_lookup' });
      }
      if (!token) throw new Error('reCAPTCHA unavailable');

      const lookup = firebase.functions().httpsCallable('userService-accountLookupHandler-lookupAccountIdentity');
      const res = await lookup({ identifier, token, action: 'account_lookup' });
      const data = res.data || {};
      onResolved({
        status: data.status || 'not_found',
        providers: data.providers || [],
        hasPhoneCredential: !!data.hasPhoneCredential,
        identifier,
      });
    } catch (err) {
      console.warn('[identity-first] lookup failed:', err);
      // Never trap the user: surface a soft error and point them at the escape hatch.
      setError('We couldn’t check that just now — tap “More sign-in options” to continue.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '0 24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size={10}>
        <Text style={{ color: '#ddd', fontSize: 14 }}>
          {`Enter your ${mode === 'phone' ? 'mobile number' : mode === 'email' ? 'email' : 'email or mobile'} to ${isSignup ? 'get started' : 'continue'}`}
        </Text>

        <Input.Group compact style={{ display: 'flex' }}>
          {isPhone && (
            <Select
              value={country.code}
              onChange={(code) => {
                setCountryTouched(true);
                setCountry(COUNTRIES.find((c) => c.code === code) || COUNTRIES[0]);
              }}
              size="large"
              style={{ width: 110, flexShrink: 0 }}
              disabled={loading}
              dropdownMatchSelectWidth={false}
            >
              {COUNTRIES.map((c) => (
                <Option key={c.code} value={c.code}>
                  {c.flag} {c.dial}
                </Option>
              ))}
            </Select>
          )}
          <Input
            placeholder={isPhone ? getPlaceholder(country.code) : (mode === 'email' ? 'name@email.com' : 'name@email.com or mobile')}
            value={raw}
            onChange={handleChange}
            onPressEnter={handleContinue}
            size="large"
            style={{ ...inputStyle, flex: 1 }}
            disabled={loading}
            type={mode === 'phone' ? 'tel' : (mode === 'email' ? 'email' : 'text')}
            inputMode={mode === 'phone' ? 'tel' : undefined}
            autoComplete={mode === 'phone' ? 'tel' : 'username'}
          />
        </Input.Group>

        <Button
          type="primary"
          block
          size="large"
          onClick={handleContinue}
          loading={loading}
          disabled={loading}
          style={{ backgroundColor: '#ff0098', borderColor: '#ff0098' }}
        >
          Continue
        </Button>

        {error && (
          <Text type="danger" style={{ fontSize: 12, display: 'block' }}>
            {error}
          </Text>
        )}

        {mode && onSwitchMode && (
          <Button
            type="text"
            block
            onClick={onSwitchMode}
            disabled={loading}
            style={{ color: '#bbb', fontSize: 13 }}
          >
            {mode === 'phone' ? 'Use my email instead' : 'Use my mobile number instead'}
          </Button>
        )}

        <Button
          type="text"
          block
          onClick={onSkip}
          disabled={loading}
          style={{ color: '#888', fontSize: 13 }}
        >
          More sign-in options
        </Button>
      </Space>
    </div>
  );
}
